import { ConnectedProps, connect, useDispatch } from "react-redux";
import {
  actions as transactionActions,
  transactionsSelector,
} from "reducers/transactions";
import { FilterTransaction } from "pages/transaction/filterTransaction/FilterTransaction";
import moment from "moment";
import * as yup from "yup";
import { DATE_FORMATS, DAYS_LIMIT } from "utils/constants";
import { VALIDATION_REGEX } from "utils/constants";
import { AppDispatch, RootState } from "store";
import {
  FilterTransactionsType,
  FilterTransactionsValidationSchemaType,
} from "sagas/types/transactions";
import {
  FilterContainerPropsType,
  FilterDateType,
  FilterValuesType,
} from "./types";
import { SetFilterType } from "reducers/types/transaction";
import { regionSelector, actions as regionsActions } from "reducers/regions";
import { DateType } from "utils/types";
import { useTranslation } from "react-i18next";
import "./i18n";
import useBasicData from "hooks/useBasicData";

const FilterTransactionContainer = (
  props: FilterContainerPropsType & ConnectorProps
): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch(); 
  const { regions } = useBasicData(["regions"]);

  const { origins } = useBasicData(["origins"]);

  const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  const getDateRange = (selectedDate: string): string => {
    const dateObj: Date = parseDate(moment(selectedDate, 'DD/MM/YY').format('DD/MM/YYYY'));
    const maxDateObj = dateObj.setDate(dateObj.getDate() + DAYS_LIMIT);
    const maxDateStr: string = moment(maxDateObj).format('DD/MM/YYYY');
    return maxDateStr;
  };

  const formatDate = (date: DateType): FilterDateType => {
    let result: FilterDateType = {};
    if (date) {
      result.date = date;
      if (moment(date.from, DATE_FORMATS.shortDate).isValid()) {
        result.minSla = moment(
          date.from,
          DATE_FORMATS.shortDate
        ).format(DATE_FORMATS.shortISODate);
        result.maxSla = moment(
          date.until,
          DATE_FORMATS.shortDate
        ).format(DATE_FORMATS.shortISODate);
      }
    }
    return result;
  };

  const onUpdateSla = async (minSla: Date, maxSla: Date) => {
    const paramMinSla = moment(minSla, 'DD/MM/YY').format('YYYY-MM-DD');
    const paramMaxSla = moment(maxSla, 'DD/MM/YY').format('YYYY-MM-DD');
    const newFiltersExtra = { 
      minSla: paramMinSla, 
      maxSla: paramMaxSla 
    }
    if(minSla && !maxSla) return;
    else {
      dispatch(regionsActions.getOriginsFetch(minSla && maxSla ? { ...newFiltersExtra } : {}));
    }
  }

  const onUpdateWorkzones = (regionsStr: string) => {
    const regionsSelected = regionsStr?.split(",") || [];
    if (
      regionsStr &&
      regionsSelected.length === 1 &&
      regionsSelected[0] !== props?.filters?.actualRegion
    ) {
      props.setActualRegion(regionsSelected[0]);
      props.getWorkzones(regionsSelected[0]);
    }
  };
  const takeFilters = (values: FilterValuesType) => {
    let formatedDate = formatDate(values.date);
    return {
      ...formatedDate,
      state: values.state === "" ? undefined : values.state,
      regions: values.regions === "" ? undefined : values.regions,
      workZones: values.workZones === "" ? undefined : values.workZones,
      origins: values.origins === "" ? undefined : values.origins,
      pickerId: values.pickerId,
      transactionCode: values.transactionCode,
      tripId: values.tripId,
      orderNumber: values.orderNumber,
      limit: props.limit,
      sku: values.sku,
    };
  };

  const onSubmit = (values: FilterValuesType) => {
    let filtersApplied = takeFilters(values);
    props.getTransactions({ ...(filtersApplied || {}), offset: 0 });
    props.setFilters({...filtersApplied, actualRegion: values.regions, actualWorkzone: values.workZones});
  };

  const validationSchema: yup.SchemaOf<FilterTransactionsValidationSchemaType> =
    yup.object({
      transactionCode: yup
        .string()
        .min(6, t("filterTransactions:codeTransactionError"))
        .matches(
          VALIDATION_REGEX.regTransactionCode,
          t("filterTransactions:codeTransactionError")
        ),
      tripId: yup
        .string()
        .matches(
          VALIDATION_REGEX.regTransactionCode,
          t("filterTransactions:tripCodeError")
        ),
      pickerId: yup
        .string()
        .matches(VALIDATION_REGEX.regPickerId, t("validations:numbersOnly")),
      sku: yup
        .string()
        .test('max-commas', t("filterTransactions:skuError"), value => {
          if (!value) return true;
          return value.split(',').length <= 10;
        }),
    });

  return (
    <FilterTransaction
      {...props}
      regions={regions}
      origins={origins}
      onSubmit={onSubmit}
      onUpdateWorkzones={onUpdateWorkzones}
      validationSchema={validationSchema}
      getDateRange={getDateRange}
      onUpdateSla={onUpdateSla}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  origins: regionSelector(state).origins,
  filters: transactionsSelector(state).filters,
  workzones: regionSelector(state).workzones,
  fetchingWorkzones: regionSelector(state).fetchingWorkzones,
  fetchingOrigins: regionSelector(state).fetchingOrigins,
  limit: transactionsSelector(state).limit,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  reset: () => {
    dispatch(transactionActions.reset());
  },
  getTransactions: (params: FilterTransactionsType) => {
    dispatch(transactionActions.getTransactionsRequest(params));
  },
  setFilters: (filters: SetFilterType) => {
    dispatch(transactionActions.setTransactionFilters(filters));
  },
  getWorkzones: (regionId: string) => {
    dispatch(regionsActions.getWorkzonesFetch(regionId));
  },
  getOrigins: (minSla: string, maxSla: string) => {
    dispatch(regionsActions.getOriginsFetch({minSla, maxSla}));
  },
  setActualRegion: (regionId: string) => {
    dispatch(transactionActions.setActualRegion(regionId));
  },
  setActualWorkzone: (workzoneId: string) => {
    dispatch(transactionActions.setActualWorkzone(workzoneId));
  },
  clearWorkzones: () => {
    dispatch(regionsActions.clearWorkzones());
  },
  clearOrigins: () => {
    dispatch(regionsActions.clearOrigins());
  },
  setCollapseTable: (collapse: boolean) => {
    dispatch(transactionActions.setCollapseTable(collapse));
  },
});
const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(FilterTransactionContainer);
